import { Controller } from "@hotwired/stimulus"

// Subscribes/unsubscribes this browser to Web Push and syncs the subscription
// with the server (PushSubscriptionsController). The actual push *send* needs a
// server-side library (web-push gem) + VAPID keys — see FD-101 TODO — but the
// browser-side subscription flow is fully wired here.
//
// The VAPID public key is read from <meta name="vapid-public-key">. When it's
// absent (no keys provisioned yet) the controls show a clear message instead of
// failing silently.
export default class extends Controller {
  static values = { createUrl: String, destroyUrl: String }
  static targets = ["enableBtn", "disableBtn", "error", "unsupported"]

  connect() {
    if (!this.isSupported()) {
      this.show(this.unsupportedTarget)
      this.enableBtnTarget.disabled = true
      return
    }
    this.refreshState()
  }

  isSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  async refreshState() {
    const sub = await this.currentSubscription()
    if (sub) {
      this.hide(this.enableBtnTarget)
      this.show(this.disableBtnTarget)
    } else {
      this.show(this.enableBtnTarget)
      this.hide(this.disableBtnTarget)
    }
  }

  async currentSubscription() {
    const reg = await navigator.serviceWorker.ready
    return reg.pushManager.getSubscription()
  }

  async subscribe() {
    this.clearError()
    try {
      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.fail("Notification permission was not granted.")
        return
      }

      const key = this.vapidPublicKey()
      if (!key) {
        this.fail("Push isn't fully configured on the server yet (missing VAPID key).")
        return
      }

      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(key),
      })

      await this.post(this.createUrlValue, { subscription: sub.toJSON() })
      this.refreshState()
    } catch (err) {
      this.fail(`Could not enable push: ${err.message}`)
    }
  }

  async unsubscribe() {
    this.clearError()
    try {
      const sub = await this.currentSubscription()
      if (sub) {
        const endpoint = sub.endpoint
        await sub.unsubscribe()
        await this.post(this.destroyUrlValue, { endpoint }, "DELETE")
      }
      this.refreshState()
    } catch (err) {
      this.fail(`Could not disable push: ${err.message}`)
    }
  }

  async post(url, body, method = "POST") {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      body: JSON.stringify(body),
      credentials: "same-origin",
    })
    if (!response.ok && response.status !== 204) {
      throw new Error(`server responded ${response.status}`)
    }
  }

  vapidPublicKey() {
    return document.querySelector('meta[name="vapid-public-key"]')?.content || ""
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // VAPID keys are URL-safe base64; the Push API needs a Uint8Array.
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i)
    return output
  }

  fail(message) {
    this.errorTarget.textContent = message
    this.show(this.errorTarget)
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.hide(this.errorTarget)
  }

  show(el) { el?.classList.remove("hidden") }
  hide(el) { el?.classList.add("hidden") }
}
